import { VideoProjectKanbanColumnState } from "@/components/VideoProjectKanban";
import { VideoProjectQueueSearchParams, VideoProjectServices } from "@/services/bedrock";

import { COACH_KANBAN_COLUMNS, CoachKanbanColumnDef, CoachKanbanColumnId } from "./coachKanbanColumns";

const COACH_KANBAN_COLUMN_PAGE_SIZE = 10;

const COACH_KANBAN_EXTRA_FIELDS = {
  needsAck: true,
} as const;

export const COACH_KANBAN_COLUMN_SORT = { sort: "status_date", order: "desc" as const };

export type CoachKanbanBoardFilters = Pick<
  VideoProjectQueueSearchParams,
  "playerId" | "createdAfter" | "createdBefore"
>;

export interface CoachKanbanColumnFetchResult extends VideoProjectKanbanColumnState {
  page: number;
}

type CoachKanbanBoardData = Record<CoachKanbanColumnId, CoachKanbanColumnFetchResult>;

export function coachKanbanBoardQueryKey(filters: CoachKanbanBoardFilters) {
  return ["videoProjects", "coachKanbanBoard", filters] as const;
}

export function coachKanbanIndexParams(
  column: CoachKanbanColumnDef,
  filters: CoachKanbanBoardFilters,
  page: number,
  perPage = COACH_KANBAN_COLUMN_PAGE_SIZE
): VideoProjectQueueSearchParams & { extraFields: typeof COACH_KANBAN_EXTRA_FIELDS } {
  return {
    playerId: filters.playerId,
    ...(filters.createdAfter ? { createdAfter: filters.createdAfter } : {}),
    ...(filters.createdBefore ? { createdBefore: filters.createdBefore } : {}),
    status: column.statuses,
    page,
    perPage,
    sort: COACH_KANBAN_COLUMN_SORT.sort,
    order: COACH_KANBAN_COLUMN_SORT.order,
    extraFields: COACH_KANBAN_EXTRA_FIELDS,
  };
}

async function fetchCoachKanbanColumnSlice(
  column: CoachKanbanColumnDef,
  filters: CoachKanbanBoardFilters,
  page: number,
  signal?: AbortSignal
): Promise<CoachKanbanColumnFetchResult> {
  const response = await VideoProjectServices.index(
    coachKanbanIndexParams(column, filters, page),
    signal ? { signal } : undefined
  );
  const currentPage = response.pagination.page ?? page;
  const totalPages = response.pagination.totalPages ?? 0;

  return {
    projects: response.content,
    totalCount: response.pagination.totalCount ?? 0,
    page: currentPage,
    hasMore: currentPage < totalPages,
  };
}

export async function fetchCoachKanbanBoard(
  filters: CoachKanbanBoardFilters,
  options?: { signal?: AbortSignal }
): Promise<CoachKanbanBoardData> {
  const columnResults = await Promise.all(
    COACH_KANBAN_COLUMNS.map(async column => {
      const result = await fetchCoachKanbanColumnSlice(column, filters, 1, options?.signal);
      return [column.id, result] as const;
    })
  );

  return Object.fromEntries(columnResults) as CoachKanbanBoardData;
}

export async function fetchNextCoachKanbanColumnPage(
  column: CoachKanbanColumnDef,
  filters: CoachKanbanBoardFilters,
  columnData: CoachKanbanColumnFetchResult,
  signal?: AbortSignal
): Promise<CoachKanbanColumnFetchResult> {
  const nextPage = await fetchCoachKanbanColumnSlice(column, filters, columnData.page + 1, signal);
  return {
    ...nextPage,
    projects: [...columnData.projects, ...nextPage.projects],
    totalCount: nextPage.totalCount,
  };
}
